import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Calendar,
  Clock,
  Calculator,
  Wallet,
  Users,
  Receipt,
  PieChart,
  Megaphone,
  Settings,
  BarChart3,
  Target,
} from 'lucide-react';
import { servicesAPI } from '../lib/api';
import { formatDate, LP_URL } from '../lib/utils';
import CTASection from '../components/shared/CTASection';
import { usePageMeta, ServiceSchema, BreadcrumbSchema } from '../components/shared/SEO';

const iconMap = {
  Calculator, Wallet, Users, Receipt, PieChart, Megaphone, Settings, BarChart3, Target
};

const ServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageMeta({
    title: service?.title,
    description: service?.full_description || service?.short_description,
    canonical: `/servico/${slug}`,
  });

  useEffect(() => {
    loadService();
  }, [slug]);

  const loadService = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesAPI.getPosts(slug);
      setService(response.data.service);
      setPosts(response.data.posts);
    } catch (err) {
      setError('Serviço não encontrado');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-28 pb-10">
        <div className="container-apex">
          <div className="skeleton h-8 w-48 mb-4" />
          <div className="skeleton h-12 w-full max-w-2xl mb-6" />
          <div className="skeleton h-24 w-full max-w-3xl mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="skeleton h-64 w-full rounded-xl" />
            <div className="skeleton h-64 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="pt-28 pb-10">
        <div className="container-apex text-center">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            Serviço não encontrado
          </h1>
          <p className="text-slate-600 mb-8">
            O serviço que você procura não existe.
          </p>
          <Button asChild className="btn-primary">
            <Link to="/servicos">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Ver todos os serviços
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Calculator;

  return (
    <div data-testid="service-page">
      <ServiceSchema service={service} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Serviços', url: '/servicos' },
        { name: service.title },
      ]} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-white pt-28 pb-10">
        <div className="container-apex">
          <Link
            to="/servicos"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar aos serviços
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1e4e7e] to-[#007f73] flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {service.title}
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {service.full_description || service.short_description}
              </p>
              <Button asChild className="btn-primary rounded-md px-8">
                <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                  Solicitar diagnóstico gratuito
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>

            {/* Benefits */}
            {service.benefits && service.benefits.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-6">
                  O que está incluído
                </h3>
                <ul className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#007f73] mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-10 md:py-14 lg:py-16 bg-slate-50">
        <div className="container-apex">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
                Artigos sobre {service.title}
              </h2>
              <p className="text-slate-600 mt-2">
                Conteúdos para aprofundar seu conhecimento sobre este tema
              </p>
            </div>
          </div>

          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={post.featured_image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.category_names?.length > 0 ? (
                        post.category_names.slice(0, 2).map((name, i) => (
                          <span key={i} className="category-badge">{name}</span>
                        ))
                      ) : (
                        <span className="category-badge">{post.category_name}</span>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.reading_time} min
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
              <p className="text-slate-500 mb-4">
                Ainda não temos artigos publicados sobre este tema.
              </p>
              <Button asChild variant="outline" className="btn-secondary">
                <Link to="/blog">
                  Ver todos os artigos
                </Link>
              </Button>
            </div>
          )}

          {posts.length > 0 && (
            <div className="text-center mt-8">
              <Button asChild variant="outline" className="btn-secondary">
                <Link to="/blog">
                  Ver todos os artigos do blog
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title={`Precisa de ajuda com ${service.title}?`}
        description="Nossa equipe está pronta para analisar sua situação e apresentar uma proposta personalizada."
        buttonText="Falar com especialista"
      />
    </div>
  );
};

export default ServicePage;
